import clsx from "clsx";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, CircleAlert, Mail } from "lucide-react";
import { type SubmitEvent, useEffect, useRef, useState } from "react";
import { useSearchParams } from "wouter";
import { api } from "../lib/api";

interface CodeSent {
    expiresInMs: number;
    resendInMs: number;
}

/**
 * Current time, re-rendering every second while `active`. Drives the countdowns. Restarts
 * whenever `since` changes (a new code), so the first tick shows the full time, not a second over.
 */
function useNow(active: boolean, since: unknown) {
    const [now, setNow] = useState(Date.now);
    useEffect(() => {
        if (!active) return;
        setNow(Date.now());
        const id = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(id);
    }, [active, since]);
    return now;
}

const mmss = (ms: number) => {
    const s = Math.max(0, Math.ceil(ms / 1000));
    return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
};

export function Login() {
    const [params] = useSearchParams();
    const qc = useQueryClient();
    const [email, setEmail] = useState(params.get("email") ?? "");
    const [code, setCode] = useState("");
    // Local deadlines, set from the durations the server returns when it sends a code.
    const [expiresAt, setExpiresAt] = useState<number | null>(null);
    const [resendAt, setResendAt] = useState(0);
    const [resent, setResent] = useState(false);
    const connected = params.get("connected");

    const codeStep = expiresAt !== null;
    const now = useNow(codeStep, expiresAt);
    const expired = codeStep && now >= expiresAt;
    const canResend = now >= resendAt;

    const requestCode = useMutation({
        mutationFn: () => api<CodeSent>("/auth/request-code", { body: { email } }),
        onSuccess: (r) => {
            const t = Date.now();
            setResent(codeStep);
            setExpiresAt(t + r.expiresInMs);
            setResendAt(t + r.resendInMs);
            setCode("");
            verify.reset();
        },
    });
    const verify = useMutation({
        mutationFn: (value: string) => api("/auth/verify", { body: { email, code: value } }),
        onSuccess: () => qc.invalidateQueries({ queryKey: ["me"] }),
    });

    const submitCode = (value: string) => {
        if (value.length === 6 && !expired && !verify.isPending) verify.mutate(value);
    };
    const submit = (e: SubmitEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (codeStep) submitCode(code);
        else requestCode.mutate();
    };
    let submitLabel = "Email me a code";
    if (codeStep) submitLabel = verify.isPending ? "Signing in..." : "Sign in";

    const startOver = () => {
        setExpiresAt(null);
        setResent(false);
        setCode("");
        requestCode.reset();
        verify.reset();
    };

    return (
        <div className="grid min-h-full place-items-center bg-canvas p-4">
            <div className="w-full max-w-sm space-y-5">
                <div className="text-center">
                    <h1 className="m-0 text-2xl text-ink">Outback Workbench</h1>
                    <p className="m-0 mt-1 text-muted">Sign in with your ServiceM8 email</p>
                </div>

                {connected ? (
                    <div className="notice is-success">
                        <strong>{connected}</strong> is connected. Sign in with your ServiceM8 staff email to finish setting it
                        up.
                    </div>
                ) : null}

                {/* gap, not space-y: the labels' m-0 would cancel space-y's margins (Tailwind v4 puts them on each child). */}
                <form onSubmit={submit} className="card flex flex-col gap-4 p-6">
                    {codeStep ? (
                        <>
                            <div className="flex items-start gap-3 text-muted">
                                <Mail className="mt-0.5 size-4 shrink-0" />
                                <p className="m-0">
                                    {resent ? "We've sent a new code to " : "We've emailed a 6-digit code to "}
                                    <strong className="text-ink">{email}</strong>.
                                </p>
                            </div>

                            <label className="m-0 block space-y-1.5">
                                <span className="flex items-baseline justify-between text-sm">
                                    <span className="font-medium">Code</span>
                                    {!expired && <span className="font-normal text-muted tabular-nums">Expires in {mmss(expiresAt - now)}</span>}
                                </span>
                                <CodeInput
                                    value={code}
                                    disabled={expired}
                                    invalid={Boolean(verify.error) || expired}
                                    describedBy="code-error"
                                    onChange={(value) => {
                                        setCode(value);
                                        verify.reset();
                                        submitCode(value); // Sign in as soon as all 6 digits are entered (or pasted).
                                    }}
                                />
                                <FieldError
                                    id="code-error"
                                    message={expired ? "This code has expired. Request a new one below." : verify.error?.message}
                                />
                            </label>
                        </>
                    ) : (
                        <label className="m-0 block space-y-1.5">
                            <span className="text-sm font-medium">Email</span>
                            <input
                                className="input"
                                type="email"
                                autoComplete="email"
                                required
                                value={email}
                                onChange={(e) => {
                                    setEmail(e.target.value);
                                    requestCode.reset();
                                }}
                                placeholder="you@company.com"
                                aria-invalid={Boolean(requestCode.error)}
                                aria-describedby="email-error"
                            />
                            <FieldError id="email-error" message={requestCode.error?.message} />
                        </label>
                    )}

                    <button
                        type="submit"
                        className="btn-primary w-full"
                        disabled={requestCode.isPending || verify.isPending || expired || (codeStep && code.length < 6)}
                    >
                        {submitLabel}
                    </button>

                    {codeStep && (
                        <div className="space-y-2">
                            <div className="flex items-center justify-between gap-2">
                                <button type="button" className="btn-secondary px-3 py-1.5" onClick={startOver}>
                                    <ArrowLeft className="size-4" /> Different email
                                </button>
                                <button
                                    type="button"
                                    className="btn-secondary px-3 py-1.5 tabular-nums"
                                    disabled={!canResend || requestCode.isPending}
                                    onClick={() => requestCode.mutate()}
                                >
                                    {canResend ? "Resend code" : `Resend code in ${mmss(resendAt - now)}`}
                                </button>
                            </div>
                            <FieldError id="resend-error" message={requestCode.error?.message} />
                        </div>
                    )}
                </form>

                <p className="m-0 text-center text-xs text-muted">
                    Setting up a new business?{" "}
                    <a href="/servicem8/connect?from=web" className="font-semibold text-brand-ink">
                        Connect ServiceM8
                    </a>
                </p>
                <p className="m-0 flex justify-center gap-4 text-xs">
                    <a href="/privacy" className="text-muted">
                        Privacy policy
                    </a>
                    <a href="/terms" className="text-muted">
                        Terms of use
                    </a>
                </p>
            </div>
        </div>
    );
}

const CODE_LENGTH = 6;

/**
 * The code, as six boxes that fill in as it's typed (the next one highlighted). Underneath it's
 * one real field over the boxes, so pasting the whole code, the phone's "from Messages/Email"
 * suggestion (one-time-code) and the number keypad all work as usual.
 */
function CodeInput(props: Readonly<{ value: string; onChange: (value: string) => void; disabled: boolean; invalid: boolean; describedBy: string }>) {
    const [focused, setFocused] = useState(false);
    // Ready to type into: the code step is only reached by asking for a code.
    const field = useRef<HTMLInputElement>(null);
    useEffect(() => field.current?.focus(), []);
    const { value } = props;
    // The box the next digit goes in (the last one once it's full).
    const next = Math.min(value.length, CODE_LENGTH - 1);
    return (
        <div className="relative">
            <div className="grid grid-cols-6 gap-2" aria-hidden="true">
                {Array.from({ length: CODE_LENGTH }, (_, i) => {
                    let edge = "border-field-edge";
                    if (props.invalid) edge = "border-danger";
                    else if (focused && i === next) edge = "border-brand-500 ring-2 ring-brand-500/25";
                    return (
                        <div
                            key={`box-${i}`}
                            className={clsx(
                                "flex h-12 items-center justify-center rounded-(--field-radius) border bg-surface font-mono text-xl font-semibold text-ink",
                                edge,
                                props.disabled && "bg-surface-2 text-muted",
                            )}
                        >
                            {value[i] ?? ""}
                        </div>
                    );
                })}
            </div>
            <input
                ref={field}
                className="absolute inset-0 size-full cursor-text bg-transparent text-transparent caret-transparent outline-none selection:bg-transparent disabled:cursor-default"
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="\d{6}"
                maxLength={CODE_LENGTH}
                required
                disabled={props.disabled}
                value={value}
                onFocus={() => setFocused(true)}
                onBlur={() => setFocused(false)}
                onChange={(e) => props.onChange(e.target.value.replaceAll(/\D/g, "").slice(0, CODE_LENGTH))}
                aria-invalid={props.invalid}
                aria-describedby={props.describedBy}
            />
        </div>
    );
}

/** Error shown directly under the field it relates to. */
function FieldError({ id, message }: Readonly<{ id: string; message?: string }>) {
    if (!message) return null;
    return (
        <p id={id} role="alert" className="m-0 flex items-start gap-1.5 text-danger">
            <CircleAlert className="mt-0.5 size-4 shrink-0" />
            <span className="text-balance">{message}</span>
        </p>
    );
}
